import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import {
  discoverAllNFTs, fetchUserAgents, fetchOwlBalance,
  enforceBaseNetwork, getReadProvider,
} from './utils/nftDiscovery';
import { CONTRACTS, ERC721_ABI, HOOTLAND_AGENT_ABI, HOOTLAND_WORLD_ABI, OWL_SDT_ABI } from './contracts';

// ─── Tier config ──────────────────────────────────────────────────────
const TIERS = {
  120: { name: 'Agent',    color: '#dc2626', glow: 'rgba(220,38,38,0.3)',  icon: '🤖' },
  150: { name: 'Sentinel', color: '#ef4444', glow: 'rgba(239,68,68,0.3)',  icon: '⚔️' },
  200: { name: 'Overseer', color: '#f97316', glow: 'rgba(249,115,22,0.3)',   icon: '👁️' },
  300: { name: 'Elder',    color: '#b91c1c', glow: 'rgba(185,28,28,0.3)',  icon: '🔮' },
};
const getTier = (multiplier) =>
  TIERS[multiplier] || TIERS[120];

const FALLBACK_IMG = `data:image/svg+xml,${encodeURIComponent(` 
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
    <rect width="200" height="200" fill="#0d0d1a"/>
    <text x="100" y="115" font-size="80" text-anchor="middle">🦉</text>
  </svg>
`)}`;

// ─── Main App ─────────────────────────────────────────────────────────

export default function HootlandApp() {
  const [wallet, setWallet]         = useState(null);
  const [tab, setTab]               = useState('gallery'); // gallery | agents
  const [nfts, setNfts]             = useState([]);
  const [agents, setAgents]         = useState([]);
  const [owlBalance, setOwlBalance] = useState('0');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [filter, setFilter]         = useState('all');
  const [search, setSearch]         = useState('');
  const [depositModal, setDepositModal] = useState(null); // nft being deposited
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [claimModal, setClaimModal] = useState(null);
  const fetchedRef = useRef(false);

  // ── Wallet connect ──
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Please install MetaMask to use Hootland');
      return;
    }
    try {
      setError(null);
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      setWallet(accounts[0]);
    } catch (e) {
      setError(e.message || 'Failed to connect wallet');
    }
  };

  // ── Wallet disconnect ──
  const disconnectWallet = () => {
    setWallet(null);
    fetchedRef.current = false;
    setNfts([]);
    setAgents([]);
    setOwlBalance('0');
    setError(null);
  };

  // ── Add Token to Wallet ──
  const addOwlTokenToWallet = async () => {
    if (!window.ethereum || !CONTRACTS.owlSDT) return;
    
    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: CONTRACTS.owlSDT,
            symbol: 'owlSDT',
            decimals: 18,
            image: 'https://gateway.pinata.cloud/ipfs/QmYourLogoImageCID/owl-logo.png',
          },
        },
      });
      alert('🪙 owlSDT token added to your wallet!');
    } catch (error) {
      console.error('Failed to add token:', error);
      alert('Failed to add token. Please add manually:\n\n' +
            'Name: owlSDT\n' +
            'Address: ' + CONTRACTS.owlSDT + '\n' +
            'Decimals: 18');
    }
  };

  // ── Load data ──
  const loadData = useCallback(async (address) => {
    if (!address) return;
    setLoading(true);
    setError(null);
    try {
      const [nftList, agentList, bal] = await Promise.all([
        discoverAllNFTs(address),
        fetchUserAgents(address),
        fetchOwlBalance(address),
      ]);
      setNfts(nftList);
      setAgents(agentList);
      setOwlBalance(parseFloat(bal).toLocaleString(undefined, { maximumFractionDigits: 2 }));
    } catch (e) {
      setError('Failed to load wallet data. Please try again.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!wallet || fetchedRef.current) return;
    fetchedRef.current = true;
    loadData(wallet);
  }, [wallet, loadData]);

  useEffect(() => {
    fetchedRef.current = false;
    setNfts([]);
    setAgents([]);
  }, [wallet]);

  // Listen for account changes
  useEffect(() => {
    if (!window.ethereum) return;
    const handler = (accounts) => {
      setWallet(accounts[0] || null);
      fetchedRef.current = false;
    };
    window.ethereum.on('accountsChanged', handler);
    return () => window.ethereum.removeListener('accountsChanged', handler);
  }, []);

  // ── Filtered NFTs ──
  const collections = [...new Set(nfts.map(n => n.collectionName))];
  const displayedNFTs = nfts.filter(nft => {
    if (filter === 'registered') return nft.isRegistered && !nft.alreadyDeposited;
    if (filter !== 'all') return nft.collectionName === filter;
    const q = search.toLowerCase();
    if (!q) return true;
    return nft.name?.toLowerCase().includes(q) ||
           nft.collectionName?.toLowerCase().includes(q) ||
           nft.tokenId?.toString().includes(q);
  }).filter(nft => {
    if (!search) return true;
    const q = search.toLowerCase();
    return nft.name?.toLowerCase().includes(q) ||
           nft.collectionName?.toLowerCase().includes(q) ||
           nft.tokenId?.toString().includes(q);
  });

  const depositableCount = nfts.filter(n => n.isRegistered && !n.alreadyDeposited).length;
  const totalPending = agents.reduce((sum, a) => sum + parseFloat(a.pendingEarnings || 0), 0);

  return (
    <div style={s.root}>
      {/* Background grid */}
      <div style={s.gridBg} />

      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.logoIcon}>🦉</span>
          <span style={s.logoText}>HOOTLAND</span>
        </div>
        <div style={s.headerCenter}>
          <a href="/mint" style={s.mintLink}>Mint Agents →</a>
          <a href="/world" style={s.mintLink}>World →</a>
          <a href="/about" style={s.mintLink}>About →</a>
          <a href="/tokenomics" style={s.mintLink}>Tokenomics →</a>
        </div>

        {wallet ? (
          <div style={s.headerRight}>
            <div style={s.owlBal}>
              <span style={s.owlIcon}>🪙</span>
              <span style={s.owlAmt}>{owlBalance} OWL</span>
              <button 
                style={s.addTokenBtn} 
                onClick={() => addOwlTokenToWallet()}
                title="Add owlSDT to MetaMask"
              >
                +
              </button>
            </div>
            {totalPending > 0 && (
              <div style={s.pendingBadge}>
                +{totalPending.toFixed(2)} pending
              </div>
            )}
            <div style={s.walletAddr}>
              {wallet.slice(0,6)}…{wallet.slice(-4)}
            </div>
            <button style={s.disconnectBtn} onClick={disconnectWallet}>
              Disconnect
            </button>
          </div>
        ) : (
          <button style={s.connectBtn} onClick={() => window.location.href='/mint'}>
            Mint Agents
          </button>
        )}
      </header>

      {/* Error banner */}
      {error && (
        <div style={s.errorBanner}>
          ⚠️ {error}
          <button style={s.errorClose} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Not connected */}
      {!wallet && (
        <div style={s.splash}>
          <div style={s.splashOwl}>🦉</div>
          <div style={s.splashTitle}>Deploy Your NFTs as Agents</div>
          <div style={s.splashSub}>
            Connect your wallet to discover your NFTs on Base,<br />
            deposit them into Hootland and earn owlSDT passively.
          </div>
          <div style={s.agentLink}>
            <a href="/HOOTLAND.md" target="_blank" rel="noopener noreferrer" style={s.agentLinkText}>
              🤖 Are you an Agent? Click here for operation manual
            </a>
          </div>
          <button style={s.splashBtn} onClick={() => window.location.href='/mint'}>
            Mint Agent →
          </button>
          <div style={s.splashStats}>
            <div style={s.splashStat}><span>10</span> owlSDT / hr base rate</div>
            <div style={s.splashStat}><span>Up to 3x</span> multiplier with Elder tier</div>
          </div>
        </div>
      )}

      {/* Main content */}
      {wallet && (
        <main style={s.main}>

          {/* Tabs */}
          <div style={s.tabs}>
            <button
              style={{ ...s.tab, ...(tab === 'gallery' ? s.tabActive : {}) }}
              onClick={() => setTab('gallery')}
            >
              Your NFTs
              <span style={s.tabBadge}>{nfts.length}</span>
            </button>
            <button
              style={{ ...s.tab, ...(tab === 'agents' ? s.tabActive : {}) }}
              onClick={() => setTab('agents')}
            >
              Active Agents
              <span style={s.tabBadge}>{agents.length}</span>
            </button>
            {depositableCount > 0 && (
              <div style={s.depositableHint}>
                🟢 {depositableCount} NFT{depositableCount > 1 ? 's' : ''} ready to deploy
              </div>
            )}
          </div>

          {loading ? (
            <LoadingState />
          ) : tab === 'gallery' ? (
            <GalleryTab
              nfts={displayedNFTs}
              allNfts={nfts}
              collections={collections}
              filter={filter}
              search={search}
              onFilterChange={setFilter}
              onSearchChange={setSearch}
              onDeposit={setDepositModal}
            />
          ) : (
            <AgentsTab
              agents={agents}
              onWithdraw={setWithdrawModal}
              onClaim={setClaimModal}
              onRefresh={() => { fetchedRef.current = false; loadData(wallet); }}
            />
          )}
        </main>
      )}

      {/* Deposit Modal */}
      {depositModal && (
        <DepositModal
          nft={depositModal}
          wallet={wallet}
          onClose={() => setDepositModal(null)}
          onSuccess={() => {
            setDepositModal(null);
            fetchedRef.current = false;
            loadData(wallet);
            setTab('agents');
          }}
        />
      )}

      {/* Withdraw Modal */}
      {withdrawModal && (
        <WithdrawModal
          agent={withdrawModal}
          wallet={wallet}
          onClose={() => setWithdrawModal(null)}
          onSuccess={() => {
            setWithdrawModal(null);
            fetchedRef.current = false;
            loadData(wallet);
          }}
        />
      )}

      {/* Claim Modal */}
      {claimModal && (
        <ClaimModal
          agent={claimModal}
          wallet={wallet}
          onClose={() => setClaimModal(null)}
          onSuccess={() => {
            setClaimModal(null);
            fetchedRef.current = false;
            loadData(wallet);
          }}
        />
      )}
      
      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerContent}>
          <span style={s.footerText}>Powered by</span>
          <a 
            href="https://openclaw.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={s.footerLink}
          >
            OpenClaw
          </a>
        </div>
      </footer>
    </div>
  );
}

// ─── Gallery Tab ──────────────────────────────────────────────────────

function GalleryTab({ nfts, allNfts, collections, filter, search, onFilterChange, onSearchChange, onDeposit }) {
  const [imgErrors, setImgErrors] = useState({});

  if (allNfts.length === 0) {
    return (
      <div style={s.emptyState}>
        <div style={{ fontSize: 48 }}>🪹</div>
        <div style={s.emptyTitle}>No NFTs found on Base</div>
        <div style={s.emptySub}>
          Mint Hootland Agents with dynamic pricing and random tier reveals
        </div>
        <a href="/mint" style={s.emptyMintLink}>
          Mint Agents →
        </a>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div style={s.controls}>
        <input
          style={s.search}
          placeholder="Search NFTs..."
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
        <div style={s.filterTabs}>
          <FilterTab label="All" count={allNfts.length} active={filter === 'all'} onClick={() => onFilterChange('all')} />
          <FilterTab
            label="🟢 Deployable"
            count={allNfts.filter(n => n.isRegistered && !n.alreadyDeposited).length}
            active={filter === 'registered'}
            onClick={() => onFilterChange('registered')}
            highlight
          />
          {collections.map(col => (
            <FilterTab
              key={col}
              label={col}
              count={allNfts.filter(n => n.collectionName === col).length}
              active={filter === col}
              onClick={() => onFilterChange(col)}
            />
          ))}
        </div>
      </div>

      {nfts.length === 0 ? (
        <div style={s.emptyState}>
          <div>No NFTs match your search</div>
        </div>
      ) : (
        <div style={s.grid}>
          {nfts.map(nft => {
            const key = `${nft.contractAddress}-${nft.tokenId}`;
            const tier = getTier(nft.earningMultiplier);
            const imgSrc = imgErrors[key] ? FALLBACK_IMG : (nft.imageUrl || FALLBACK_IMG);

            return (
              <div key={key} style={{
                ...s.card,
                ...(nft.isRegistered ? { borderColor: tier.color + '66' } : {}),
                ...(nft.alreadyDeposited ? s.cardDeposited : {}),
              }}>
                {/* Status badges */}
                {nft.alreadyDeposited && (
                  <div style={{ ...s.badge, background: '#22c55e22', color: '#22c55e', borderColor: '#22c55e44' }}>
                    ✓ Deployed
                  </div>
                )}
                {nft.isRegistered && !nft.alreadyDeposited && (
                  <div style={{ ...s.badge, background: tier.glow, color: tier.color, borderColor: tier.color + '44' }}>
                    {tier.icon} {tier.name}
                  </div>
                )}

                {/* Image */}
                <div style={s.cardImg}>
                  <img
                    src={imgSrc}
                    alt={nft.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={() => setImgErrors(p => ({ ...p, [key]: true }))}
                  />
                  {nft.isRegistered && !nft.alreadyDeposited && (
                    <div style={{ ...s.cardImgGlow, background: tier.glow }} />
                  )}
                </div>

                {/* Info */}
                <div style={s.cardBody}>
                  <div style={s.cardName}>{nft.name}</div>
                  <div style={s.cardCollection}>{nft.collectionName}</div>

                  {nft.isRegistered && !nft.alreadyDeposited && (
                    <div style={s.cardEarning}>
                      ~{(10 * nft.earningMultiplier / 100).toFixed(0)} OWL/hr
                    </div>
                  )}

                  {/* Action button */}
                  {nft.alreadyDeposited ? (
                    <div style={s.deployedTag}>Active in Hootland</div>
                  ) : nft.isRegistered ? (
                    <button
                      style={{ ...s.actionBtn, background: tier.color }}
                      onClick={() => onDeposit(nft)}
                    >
                      Deploy as Agent
                    </button>
                  ) : (
                    <div style={s.unregisteredTag}>Not in registry</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Agents Tab ───────────────────────────────────────────────────────

function AgentsTab({ agents, onWithdraw, onClaim, onRefresh }) {
  if (agents.length === 0) {
    return (
      <div style={s.emptyState}>
        <div style={{ fontSize: 48 }}>🤖</div>
        <div style={s.emptyTitle}>No active agents</div>
        <div style={s.emptySub}>
          Deploy an NFT from your gallery to create your first agent
        </div>
      </div>
    );
  }

  const totalEarned  = agents.reduce((sum, a) => sum + parseFloat(a.totalEarned  || 0), 0);
  const totalPending = agents.reduce((sum, a) => sum + parseFloat(a.pendingEarnings || 0), 0);

  return (
    <div>
      {/* Summary bar */}
      <div style={s.agentSummary}>
        <div style={s.summaryItem}>
          <div style={s.summaryVal}>{agents.length}</div>
          <div style={s.summaryLabel}>Active Agents</div>
        </div>
        <div style={s.summaryDivider} />
        <div style={s.summaryItem}>
          <div style={s.summaryVal}>{totalEarned.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={s.summaryLabel}>Total OWL Earned</div>
        </div>
        <div style={s.summaryDivider} />
        <div style={s.summaryItem}>
          <div style={{ ...s.summaryVal, color: '#22c55e' }}>
            +{totalPending.toFixed(2)}
          </div>
          <div style={s.summaryLabel}>Pending OWL</div>
        </div>
        <button style={s.refreshBtn} onClick={onRefresh}>↻ Refresh</button>
      </div>

      {/* Agent cards */}
      <div style={s.agentGrid}>
        {agents.map(agent => {
          const cooldownOver = Date.now() / 1000 >= agent.lastClaimTime + 3600;
          const pending = parseFloat(agent.pendingEarnings || 0);

          return (
            <div key={agent.agentId} style={s.agentCard}>
              <div style={s.agentHeader}>
                <div style={s.agentId}>Agent #{agent.agentId}</div>
                <div style={s.agentPos}>📍 ({agent.x}, {agent.y})</div>
              </div>

              <div style={s.agentTokenInfo}>
                <div style={s.agentTokenLabel}>Original NFT</div>
                <div style={s.agentTokenVal}>
                  {agent.originalCollection.slice(0,6)}…{agent.originalCollection.slice(-4)}
                  {' '}#{agent.originalTokenId}
                </div>
              </div>

              <div style={s.agentEarnings}>
                <div style={s.earningRow}>
                  <span style={s.earningLabel}>Total earned</span>
                  <span style={s.earningVal}>
                    {parseFloat(agent.totalEarned).toLocaleString(undefined, { maximumFractionDigits: 2 })} OWL
                  </span>
                </div>
                <div style={s.earningRow}>
                  <span style={s.earningLabel}>Pending</span>
                  <span style={{ ...s.earningVal, color: pending > 0 ? '#22c55e' : '#666' }}>
                    +{pending.toFixed(4)} OWL
                  </span>
                </div>
              </div>

              {/* Earnings bar */}
              <div style={s.earningBarBg}>
                <div style={{
                  ...s.earningBarFill,
                  width: `${Math.min(100, (pending / 10) * 100)}%`,
                }} />
              </div>
              <div style={s.cooldownHint}>
                {cooldownOver ? '✅ Ready to claim' : '⏳ Cooldown active (1hr)'}
              </div>

              {/* Actions */}
              <div style={s.agentActions}>
                <button
                  style={{
                    ...s.claimBtn,
                    ...((!cooldownOver || pending === 0) ? s.btnDisabled : {}),
                  }}
                  onClick={() => onClaim(agent)}
                  disabled={!cooldownOver || pending === 0}
                >
                  Claim {pending > 0 ? `${pending.toFixed(2)} OWL` : 'Earnings'}
                </button>
                <button
                  style={s.withdrawBtn}
                  onClick={() => onWithdraw(agent)}
                >
                  Withdraw NFT
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Deposit Modal ────────────────────────────────────────────────────

function DepositModal({ nft, wallet, onClose, onSuccess }) {
  const [step, setStep] = useState('confirm'); // confirm | approving | depositing | done
  const [txHash, setTxHash] = useState(null);
  const [error, setError] = useState(null);
  const tier = getTier(nft.earningMultiplier);

  const handleDeposit = async () => {
    setError(null);
    try {
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();

      // Step 1: Approve AgentFactory to move the NFT
      setStep('approving');
      const nftContract = new ethers.Contract(nft.contractAddress, ERC721_ABI, signer);

      // Check if already approved
      const approved    = await nftContract.getApproved(nft.tokenId);
      const approvedAll = await nftContract.isApprovedForAll(wallet, ADDRESSES.AgentFactory);

      if (approved.toLowerCase() !== ADDRESSES.AgentFactory.toLowerCase() && !approvedAll) {
        const approveTx = await nftContract.approve(ADDRESSES.AgentFactory, nft.tokenId);
        await approveTx.wait();
      }

      // Step 2: depositNFT
      setStep('depositing');
      const factory = new ethers.Contract(ADDRESSES.AgentFactory, AGENT_FACTORY_ABI, signer);
      const depositTx = await factory.depositNFT(nft.contractAddress, nft.tokenId);
      setTxHash(depositTx.hash);
      await depositTx.wait();

      setStep('done');
    } catch (e) {
      console.error(e);
      setError(e.reason || e.message || 'Transaction failed');
      setStep('confirm');
    }
  };

  const stepLabels = {
    confirm:   '1. Confirm details',
    approving: '2. Approving transfer…',
    depositing:'3. Deploying agent…',
    done:      '✅ Agent deployed!',
  };

  return (
    <Overlay onClose={step === 'done' ? onSuccess : onClose}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <div style={{ ...s.modalTitle, color: tier.color }}>
            {tier.icon} Deploy as Agent
          </div>
          <div style={s.modalStep}>{stepLabels[step]}</div>
        </div>

        {/* NFT preview */}
        <div style={s.modalNFT}>
          <img
            src={nft.imageUrl || FALLBACK_IMG}
            alt={nft.name}
            style={s.modalNFTImg}
            onError={e => { e.target.src = FALLBACK_IMG; }}
          />
          <div style={s.modalNFTInfo}>
            <div style={s.modalNFTName}>{nft.name}</div>
            <div style={s.modalNFTCol}>{nft.collectionName}</div>
            <div style={s.modalNFTEarn}>
              <span>Earning rate</span>
              <span style={{ color: tier.color }}>
                ~{(10 * nft.earningMultiplier / 100).toFixed(0)} OWL/hr ({nft.earningMultiplier / 100}x)
              </span>
            </div>
          </div>
        </div>

        {/* Info boxes */}
        {step === 'confirm' && (
          <div style={s.infoBox}>
            <div style={s.infoRow}>🔒 Your NFT will be locked in AgentFactory contract</div>
            <div style={s.infoRow}>🤖 An Agent NFT will be minted to your wallet</div>
            <div style={s.infoRow}>💰 Earn owlSDT automatically — claim anytime after 1hr</div>
            <div style={s.infoRow}>↩️ Withdraw your original NFT back anytime</div>
          </div>
        )}

        {/* Progress */}
        {(step === 'approving' || step === 'depositing') && (
          <div style={s.progressBox}>
            <div style={s.spinner}>⏳</div>
            <div style={s.progressText}>
              {step === 'approving'
                ? 'Approving NFT transfer in MetaMask…'
                : 'Deploying your agent on-chain…'}
            </div>
          </div>
        )}

        {/* Done */}
        {step === 'done' && (
          <div style={s.successBox}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <div style={s.successText}>Agent deployed successfully!</div>
            <div style={s.successSub}>Your NFT is now earning owlSDT in Hootland</div>
            {txHash && (
              <a
                href={`https://basescan.org/tx/${txHash}`}
                target="_blank"
                rel="noreferrer"
                style={s.txLink}
              >
                View on Basescan →
              </a>
            )}
          </div>
        )}

        {error && <div style={s.modalError}>❌ {error}</div>}

        {/* Buttons */}
        <div style={s.modalBtns}>
          {step === 'done' ? (
            <button style={{ ...s.actionBtn, background: '#22c55e' }} onClick={onSuccess}>
              View My Agents
            </button>
          ) : step === 'confirm' ? (
            <>
              <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
              <button style={{ ...s.actionBtn, background: tier.color }} onClick={handleDeposit}>
                Approve & Deploy
              </button>
            </>
          ) : null}
        </div>
      </div>
    </Overlay>
  );
}

// ─── Withdraw Modal ───────────────────────────────────────────────────

function WithdrawModal({ agent, wallet, onClose, onSuccess }) {
  const [step, setStep]   = useState('confirm');
  const [txHash, setTxHash] = useState(null);
  const [error, setError] = useState(null);

  const handleWithdraw = async () => {
    setError(null);
    setStep('withdrawing');
    try {
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const factory  = new ethers.Contract(ADDRESSES.AgentFactory, AGENT_FACTORY_ABI, signer);
      const tx = await factory.withdrawNFT(agent.agentId);
      setTxHash(tx.hash);
      await tx.wait();
      setStep('done');
    } catch (e) {
      setError(e.reason || e.message || 'Transaction failed');
      setStep('confirm');
    }
  };

  return (
    <Overlay onClose={step === 'done' ? onSuccess : onClose}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>↩️ Withdraw NFT</div>
          <div style={s.modalStep}>Agent #{agent.agentId}</div>
        </div>

        {step === 'confirm' && (
          <div style={s.infoBox}>
            <div style={s.infoRow}>⚠️ Withdrawing will stop this agent from earning</div>
            <div style={s.infoRow}>💰 Any pending earnings will be automatically claimed first</div>
            <div style={s.infoRow}>🔓 Your original NFT will be returned to your wallet</div>
            <div style={s.infoRow}>🔥 The Agent NFT will be burned</div>
            <div style={{ ...s.infoRow, color: '#22c55e' }}>
              Pending: +{parseFloat(agent.pendingEarnings || 0).toFixed(4)} OWL will be claimed
            </div>
          </div>
        )}

        {step === 'withdrawing' && (
          <div style={s.progressBox}>
            <div style={s.spinner}>⏳</div>
            <div style={s.progressText}>Processing withdrawal…</div>
          </div>
        )}

        {step === 'done' && (
          <div style={s.successBox}>
            <div style={{ fontSize: 40 }}>✅</div>
            <div style={s.successText}>NFT returned to your wallet</div>
            {txHash && (
              <a href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noreferrer" style={s.txLink}>
                View on Basescan →
              </a>
            )}
          </div>
        )}

        {error && <div style={s.modalError}>❌ {error}</div>}

        <div style={s.modalBtns}>
          {step === 'done' ? (
            <button style={{ ...s.actionBtn, background: '#22c55e' }} onClick={onSuccess}>Done</button>
          ) : step === 'confirm' ? (
            <>
              <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
              <button style={{ ...s.actionBtn, background: '#f87171' }} onClick={handleWithdraw}>
                Withdraw NFT
              </button>
            </>
          ) : null}
        </div>
      </div>
    </Overlay>
  );
}

// ─── Claim Modal ──────────────────────────────────────────────────────

function ClaimModal({ agent, wallet, onClose, onSuccess }) {
  const [step, setStep]   = useState('confirm');
  const [txHash, setTxHash] = useState(null);
  const [error, setError] = useState(null);
  const pending = parseFloat(agent.pendingEarnings || 0);

  const handleClaim = async () => {
    setError(null);
    setStep('claiming');
    try {
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const factory  = new ethers.Contract(ADDRESSES.AgentFactory, AGENT_FACTORY_ABI, signer);
      const tx = await factory.claimEarnings(agent.agentId);
      setTxHash(tx.hash);
      await tx.wait();
      setStep('done');
    } catch (e) {
      setError(e.reason || e.message || 'Transaction failed');
      setStep('confirm');
    }
  };

  return (
    <Overlay onClose={step === 'done' ? onSuccess : onClose}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>💰 Claim Earnings</div>
          <div style={s.modalStep}>Agent #{agent.agentId}</div>
        </div>

        {step === 'confirm' && (
          <div style={s.claimPreview}>
            <div style={s.claimAmount}>{pending.toFixed(4)}</div>
            <div style={s.claimSymbol}>owlSDT</div>
            <div style={s.claimSub}>will be minted to your wallet</div>
          </div>
        )}

        {step === 'claiming' && (
          <div style={s.progressBox}>
            <div style={s.spinner}>⏳</div>
            <div style={s.progressText}>Claiming earnings…</div>
          </div>
        )}

        {step === 'done' && (
          <div style={s.successBox}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <div style={s.successText}>{pending.toFixed(4)} OWL claimed!</div>
            {txHash && (
              <a href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noreferrer" style={s.txLink}>
                View on Basescan →
              </a>
            )}
          </div>
        )}

        {error && <div style={s.modalError}>❌ {error}</div>}

        <div style={s.modalBtns}>
          {step === 'done' ? (
            <button style={{ ...s.actionBtn, background: '#22c55e' }} onClick={onSuccess}>Done</button>
          ) : step === 'confirm' ? (
            <>
              <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
              <button style={{ ...s.actionBtn, background: '#f59e0b' }} onClick={handleClaim}>
                Claim {pending.toFixed(2)} OWL
              </button>
            </>
          ) : null}
        </div>
      </div>
    </Overlay>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      {children}
    </div>
  );
}

function FilterTab({ label, count, active, onClick, highlight }) {
  return (
    <button
      style={{
        ...s.filterTab,
        ...(active ? s.filterTabActive : {}),
        ...(highlight && !active ? { borderColor: '#22c55e66', color: '#22c55e' } : {}),
      }}
      onClick={onClick}
    >
      {label}
      {count > 0 && <span style={s.filterCount}>{count}</span>}
    </button>
  );
}

function LoadingState() {
  return (
    <div style={s.loadingState}>
      <div style={s.loadingOwl}>🦉</div>
      <div style={s.loadingText}>Scouting your NFTs on Base…</div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────

const s = {
  root: {
    minHeight: '100vh',
    background: '#0a0a0f',
    color: '#e2e8f0',
    fontFamily: '"IBM Plex Mono", "Fira Code", monospace',
    position: 'relative',
    overflowX: 'hidden',
  },
  gridBg: {
    position: 'fixed', inset: 0, zIndex: 0,
    backgroundImage: `
      linear-gradient(rgba(220,38,38,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(220,38,38,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    pointerEvents: 'none',
  },
  header: {
    position: 'relative', zIndex: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '1px solid rgba(220,38,38,0.1)',
    background: 'rgba(10,10,15,0.8)',
    backdropFilter: 'blur(12px)',
  },
  logo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: { fontSize: 28 },
  logoText: { fontSize: 20, fontWeight: 700, letterSpacing: '0.15em', color: '#dc2626' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  headerCenter: { flex: 1, textAlign: 'center' },
  mintLink: {
    color: '#dc2626', textDecoration: 'none',
    fontSize: 13, fontWeight: 600,
    padding: '6px 12px',
    border: '1px solid rgba(220,38,38,0.3)',
    borderRadius: 6,
    background: 'rgba(220,38,38,0.1)',
    transition: 'all 0.2s',
  },
  owlBal: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(220,38,38,0.08)',
    border: '1px solid rgba(220,38,38,0.2)',
    borderRadius: 8, padding: '6px 12px',
  },
  owlIcon: { fontSize: 16 },
  owlAmt: { fontSize: 14, fontWeight: 600, color: '#dc2626' },
  addTokenBtn: {
    background: 'rgba(220,38,38,0.1)',
    border: '1px solid rgba(220,38,38,0.3)',
    borderRadius: 4,
    padding: '2px 6px',
    fontSize: 12,
    cursor: 'pointer',
    color: '#dc2626',
    minWidth: 20,
    height: 24,
  },
  pendingBadge: {
    fontSize: 12, color: '#22c55e',
    background: 'rgba(34,197,94,0.1)',
    border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 6, padding: '4px 8px',
  },
  walletAddr: {
    fontSize: 13, color: '#64748b',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '6px 10px',
  },
  connectBtn: {
    background: '#dc2626', color: '#0a0a0f',
    border: 'none', borderRadius: 8,
    padding: '10px 20px', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  disconnectBtn: {
    background: 'rgba(220,38,38,0.1)', color: '#dc2626',
    border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  errorBanner: {
    position: 'relative', zIndex: 10,
    background: 'rgba(220,38,38,0.1)',
    border: '1px solid rgba(220,38,38,0.3)',
    color: '#dc2626', padding: '12px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  errorClose: {
    background: 'none', border: 'none', color: '#dc2626',
    cursor: 'pointer', fontSize: 16,
  },

  // Splash
  splash: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: 'calc(100vh - 65px)',
    padding: 40, textAlign: 'center',
  },
  splashOwl: { fontSize: 80, marginBottom: 20 },
  splashTitle: { fontSize: 36, fontWeight: 700, color: '#7eb8f7', marginBottom: 16, letterSpacing: '-0.02em' },
  splashSub: { fontSize: 16, color: '#64748b', lineHeight: 1.7, marginBottom: 32 },
  splashBtn: {
    background: '#7eb8f7', color: '#07070f',
    border: 'none', borderRadius: 10,
    padding: '14px 32px', fontSize: 16, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', marginBottom: 32,
  },
  splashStats: { display: 'flex', gap: 32 },
  splashStat: {
    fontSize: 13, color: '#475569',
    '& span': { color: '#7eb8f7', fontWeight: 700 },
  },
  agentLink: {
    marginBottom: 24,
    padding: '12px 20px',
    background: 'rgba(126,184,247,0.1)',
    borderRadius: 8,
    border: '1px solid rgba(126,184,247,0.2)',
  },
  agentLinkText: {
    color: '#7eb8f7',
    fontSize: 14,
    textDecoration: 'none',
    fontWeight: 500,
  },

  // Main
  main: {
    position: 'relative', zIndex: 1,
    maxWidth: 1200, margin: '0 auto', padding: '24px 24px 60px',
  },
  tabs: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 24,
    borderBottom: '1px solid rgba(126,184,247,0.1)',
    paddingBottom: 16,
  },
  tab: {
    background: 'none', border: '1px solid transparent',
    borderRadius: 8, padding: '8px 16px',
    color: '#475569', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: 8,
    transition: 'all 0.15s',
  },
  tabActive: {
    background: 'rgba(126,184,247,0.1)',
    borderColor: 'rgba(126,184,247,0.3)',
    color: '#7eb8f7',
  },
  tabBadge: {
    background: 'rgba(126,184,247,0.15)',
    borderRadius: 10, padding: '1px 7px', fontSize: 11,
  },
  depositableHint: {
    marginLeft: 'auto', fontSize: 12, color: '#22c55e',
    background: 'rgba(34,197,94,0.08)',
    border: '1px solid rgba(34,197,94,0.2)',
    borderRadius: 6, padding: '4px 10px',
  },

  // Controls
  controls: { marginBottom: 16 },
  search: {
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(126,184,247,0.15)',
    borderRadius: 8, padding: '10px 14px',
    color: '#e2e8f0', fontSize: 14, marginBottom: 12,
    outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  filterTabs: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterTab: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20, padding: '4px 12px',
    color: '#64748b', fontSize: 12, cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  filterTabActive: {
    background: 'rgba(126,184,247,0.1)',
    borderColor: 'rgba(126,184,247,0.4)',
    color: '#7eb8f7',
  },
  filterCount: {
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '1px 6px', fontSize: 10,
  },

  // Grid
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
  },
  card: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12, overflow: 'hidden',
    transition: 'transform 0.15s, box-shadow 0.15s',
    position: 'relative',
  },
  cardDeposited: { opacity: 0.6 },
  badge: {
    position: 'absolute', top: 8, left: 8, zIndex: 2,
    border: '1px solid', borderRadius: 6,
    padding: '2px 8px', fontSize: 10, fontWeight: 700,
    fontFamily: 'inherit',
  },
  cardImg: {
    aspectRatio: '1', background: '#0d0d1a',
    position: 'relative', overflow: 'hidden',
  },
  cardImgGlow: {
    position: 'absolute', inset: 0,
    mixBlendMode: 'overlay', pointerEvents: 'none',
  },
  cardBody: { padding: '10px 12px' },
  cardName: {
    fontSize: 13, fontWeight: 600, color: '#e2e8f0',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    marginBottom: 2,
  },
  cardCollection: {
    fontSize: 11, color: '#475569',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    marginBottom: 8,
  },
  cardEarning: {
    fontSize: 11, color: '#22c55e', marginBottom: 8,
  },
  actionBtn: {
    width: '100%', border: 'none', borderRadius: 6,
    padding: '8px', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', color: '#0a0a0f',
  },
  deployedTag: {
    textAlign: 'center', fontSize: 11, color: '#dc2626', padding: '6px 0',
  },
  unregisteredTag: {
    textAlign: 'center', fontSize: 11, color: '#64748b', padding: '6px 0',
  },

  // Agents
  agentSummary: {
    display: 'flex', alignItems: 'center', gap: 0,
    background: 'rgba(220,38,38,0.05)',
    border: '1px solid rgba(220,38,38,0.15)',
    borderRadius: 12, padding: '16px 24px',
    marginBottom: 20,
  },
  summaryItem: { flex: 1, textAlign: 'center' },
  summaryVal: { fontSize: 24, fontWeight: 700, color: '#dc2626' },
  summaryLabel: { fontSize: 11, color: '#475569', marginTop: 2 },
  summaryDivider: {
    width: 1, height: 40,
    background: 'rgba(220,38,38,0.15)', margin: '0 16px',
  },
  refreshBtn: {
    background: 'none', border: '1px solid rgba(220,38,38,0.2)',
    borderRadius: 6, padding: '6px 12px',
    color: '#dc2626', fontSize: 12, cursor: 'pointer',
    fontFamily: 'inherit', marginLeft: 16,
  },
  agentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  agentCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(220,38,38,0.15)',
    borderRadius: 12, padding:16,
  },
  agentHeader: {
    display: 'flex', justifyContent: 'space-between',
    marginBottom: 12,
  },
  agentId: { fontSize: 14, fontWeight: 700, color: '#dc2626' },
  agentPos: { fontSize: 11, color: '#64748b' },
  agentTokenInfo: { marginBottom: 12 },
  agentTokenLabel: { fontSize: 10, color: '#64748b', marginBottom: 2 },
  agentTokenVal: { fontSize: 12, color: '#ef4444', fontFamily: 'monospace' },
  agentEarnings: { marginBottom: 8 },
  earningRow: {
    display: 'flex', justifyContent: 'space-between',
    marginBottom: 4,
  },
  earningLabel: { fontSize: 11, color: '#64748b' },
  earningVal: { fontSize: 12, fontWeight: 600, color: '#ef4444' },
  earningBarBg: {
    height: 4, background: 'rgba(255,255,255,0.06)',
    borderRadius: 2, overflow: 'hidden', marginBottom: 6,
  },
  earningBarFill: {
    height: '100%', background: '#dc2626',
    borderRadius: 2, transition: 'width 0.5s',
  },
  cooldownHint: { fontSize: 10, color: '#64748b', marginBottom: 12 },
  agentActions: { display: 'flex', gap: 8 },
  claimBtn: {
    flex: 2, background: '#dc2626', color: '#0a0a0f',
    border: 'none', borderRadius: 6, padding: '8px',
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  withdrawBtn: {
    flex: 1, background: 'rgba(220,38,38,0.1)',
    border: '1px solid rgba(220,38,38,0.3)',
    borderRadius: 6, padding: '8px',
    color: '#dc2626', fontSize: 11, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },

  // Empty / loading
  emptyState: {
    textAlign: 'center', padding: '60px 20px',
    color: '#475569',
  },
  emptyTitle: { fontSize: 18, color: '#64748b', marginTop: 12, marginBottom: 8 },
  emptySub: { fontSize: 13, lineHeight: 1.6, marginBottom: 20 },
  emptyMintLink: {
    display: 'inline-block',
    color: '#7eb8f7', fontSize: 13,
    border: '1px solid rgba(126,184,247,0.3)',
    borderRadius: 6, padding: '8px 16px',
    textDecoration: 'none',
  },
  loadingState: {
    textAlign: 'center', padding: '80px 20px',
  },
  loadingOwl: {
    fontSize: 48,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  loadingText: { fontSize: 14, color: '#475569', marginTop: 16 },

  // Modal
  overlay: {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  },
  modal: {
    background: '#0d0d1a',
    border: '1px solid rgba(126,184,247,0.2)',
    borderRadius: 16, padding: 28,
    width: '100%', maxWidth: 440,
    fontFamily: '"IBM Plex Mono", monospace',
  },
  modalHeader: { marginBottom: 20 },
  modalTitle: { fontSize: 20, fontWeight: 700, marginBottom: 4 },
  modalStep: { fontSize: 12, color: '#475569' },
  modalNFT: {
    display: 'flex', gap: 16,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10, padding: 14, marginBottom: 16,
  },
  modalNFTImg: {
    width: 72, height: 72, borderRadius: 8, objectFit: 'cover',
    flexShrink: 0,
  },
  modalNFTInfo: { flex: 1 },
  modalNFTName: { fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 },
  modalNFTCol: { fontSize: 11, color: '#475569', marginBottom: 8 },
  modalNFTEarn: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 11,
  },
  infoBox: {
    background: 'rgba(126,184,247,0.04)',
    border: '1px solid rgba(126,184,247,0.1)',
    borderRadius: 8, padding: 14, marginBottom: 20,
  },
  infoRow: {
    fontSize: 12, color: '#64748b', lineHeight: 1.5,
    paddingBottom: 6, marginBottom: 6,
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  progressBox: {
    textAlign: 'center', padding: '24px 0 20px',
  },
  spinner: { fontSize: 32, marginBottom: 12 },
  progressText: { fontSize: 13, color: '#64748b' },
  successBox: {
    textAlign: 'center', padding: '24px 0 20px',
  },
  successText: { fontSize: 16, fontWeight: 700, color: '#22c55e', marginTop: 8 },
  successSub: { fontSize: 12, color: '#475569', marginTop: 6 },
  txLink: {
    display: 'inline-block', marginTop: 10,
    fontSize: 12, color: '#7eb8f7',
    textDecoration: 'none',
  },
  modalError: {
    background: 'rgba(248,113,113,0.08)',
    border: '1px solid rgba(248,113,113,0.2)',
    borderRadius: 6, padding: 10,
    fontSize: 12, color: '#f87171', marginBottom: 16,
  },
  modalBtns: { display: 'flex', gap: 10, marginTop: 4 },
  cancelBtn: {
    flex: 1, background: 'none',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6, padding: '10px',
    color: '#64748b', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Claim preview
  claimPreview: {
    textAlign: 'center', padding: '24px 0 20px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    marginBottom: 20,
  },
  claimAmount: { fontSize: 40, fontWeight: 700, color: '#f59e0b' },
  claimSymbol: { fontSize: 16, color: '#94a3b8', marginTop: 4 },
  claimSub: { fontSize: 12, color: '#475569', marginTop: 8 },
  
  // Footer
  footer: {
    position: 'relative', zIndex: 10,
    padding: '20px 24px',
    borderTop: '1px solid rgba(220,38,38,0.1)',
    background: 'rgba(10,10,15,0.9)',
    backdropFilter: 'blur(12px)',
    textAlign: 'center',
  },
  footerContent: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontSize: 12,
  },
  footerText: {
    color: '#64748b',
    fontSize: 12,
  },
  footerLink: {
    color: '#dc2626',
    textDecoration: 'none',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 0.2s',
  },
};
